import { create } from 'zustand';
import { devtools } from 'zustand/middleware'

// ============ Types ============
// 公共字段抽取
export interface BaseComment {
  comment: string;
  createAt: string;
  nickname: string;
  like: number;
  dislike: number;
  folded: boolean;
  deleted: boolean;
  is_author: boolean;
  author_liked: boolean;
  author_replied: boolean;
  report_num: number;
  profile: string;
  location: string;
  profile_id: string;
  upvoted: boolean;
  downvoted: boolean;
  view: number;
  media_id: string;
  followed: number;
}

// 回复类型
export interface CommentReply extends BaseComment {
  reply_id: string;
  reply_to: string;
  reply_to_userid: number;
  reply_to_profile_id: string;
  type?: string;
}

// 评论类型
export interface Comment extends BaseComment {
  appid: string;
  docid: string;
  comment_id: string;
  mp_source_type: string;
  score: number;
  reply_n: number;
  poll_id: string;
  poll_option_id: string;
  replies: CommentReply[];
  mine?: boolean; // 标识是否为当前用户的评论
  hide_reply_list?: CommentReply[];
}

// ============ Store Interface ============
interface CommentStore {
  hotComments: Comment[];
  newComments: Comment[];
  setHotComments: (comments: Comment[]) => void;
  setNewComments: (comments: Comment[]) => void;
  addHotComments: (comments: Comment[]) => void;
  addNewComments: (comments: Comment[]) => void;
  addComment: (comment: Comment) => void;
  addReply: (commentId: string, reply: CommentReply) => void;
  setReplyList: (commentId: string, replyList: CommentReply[]) => void;
  deleteComment: (commentId: string) => void;
  deleteReply: (commentId: string, replyId: string) => void;
  updateComment: (commentId: string, updates: Partial<Comment>) => void;
  updateReply: (commentId: string, replyId: string, updates: Partial<CommentReply>) => void;
  clearComments: () => void;
  getCommentById: (commentId: string) => Comment | undefined;
  getReplyById: (commentId: string, replyId: string) => CommentReply | undefined;
}

// ============ Zustand Store ============
export const useCommentStore = create<CommentStore>()(devtools((set, get) => ({
  hotComments: [],
  newComments: [],

  setHotComments: (_comments: Comment[]) => {
    const comments = _comments.map((comment) => {
      if (comment.replies && comment.replies.length > 0) {
        const replyList = comment.replies.slice(0, 1);
        const hideReplyList = comment.replies.slice(1);
        return {
          ...comment,
          replies: replyList,
          hide_reply_list: hideReplyList
        };
      }
      return comment;
    })
    set(() => ({ hotComments: comments }));
  },
  setNewComments: (_comments: Comment[]) => {
    const comments = _comments.map((comment) => {
      if (comment.replies && comment.replies.length > 0) {
        const replyList = comment.replies.slice(0, 1);
        const hideReplyList = comment.replies.slice(1);
        return {
          ...comment,
          replies: replyList,
          hide_reply_list: hideReplyList
        };
      }
      return comment;
    })
    set(() => ({ newComments: comments }));
  },
  addHotComments: (_comments: Comment[]) => {
    const comments = _comments.map((comment) => {
      if (comment.replies && comment.replies.length > 0) {
        const replyList = comment.replies.slice(0, 1);
        const hideReplyList = comment.replies.slice(1);
        return {
          ...comment,
          replies: replyList,
          hide_reply_list: hideReplyList
        };
      }
      return comment;
    })
    set((state) => {
      return {
        hotComments: [...state.hotComments, ...comments].filter((comment, index, self) =>
          index === self.findIndex((t) => t.comment_id === comment.comment_id)
        )
      }
    });
  },
  addNewComments: (_comments: Comment[]) => {
    const comments = _comments.map((comment) => {
      if (comment.replies && comment.replies.length > 0) {
        const replyList = comment.replies.slice(0, 1);
        const hideReplyList = comment.replies.slice(1);
        return {
          ...comment,
          replies: replyList,
          hide_reply_list: hideReplyList
        };
      }
      return comment;
    })
    set((state) => {
      return {
        newComments: [...state.newComments, ...comments].filter((comment, index, self) =>
          index === self.findIndex((t) => t.comment_id === comment.comment_id)
        )
      }
    });
  },
  addComment: (comment: Comment) => {
    set((state) => ({
      hotComments: [comment, ...state.hotComments],
      newComments: [comment, ...state.newComments]
    }));
  },

  addReply: (commentId: string, reply: CommentReply) => {
    console.log("🚀 ~ useCommentStore ~ commentId:", commentId, reply)
    set((state) => ({
      hotComments: state.hotComments.map((comment) => {
        if (comment.comment_id === commentId) {
          const updatedReplies = [...(comment.replies || []), reply];
          console.log("🚀 ~ hotComments:state.hotComments.map ~ updatedReplies:", updatedReplies)
          return {
            ...comment,
            replies: updatedReplies,
            reply_n: updatedReplies.length,
          };
        }
        return comment;
      }),
      newComments: state.newComments.map((comment) => {
        if (comment.comment_id === commentId) {
          const updatedReplies = [...(comment.replies || []), reply];
          console.log("🚀 ~ newComments:state.newComments.map ~ updatedReplies:", updatedReplies)
          return {
            ...comment,
            replies: updatedReplies,
            reply_n: updatedReplies.length,
          };
        }
        return comment;
      }),
    }));
  },
  setReplyList: (commentId: string, replyList: CommentReply[]) => {
    set((state) => ({
      hotComments: state.hotComments.map((comment) => {
        if (comment.comment_id === commentId) {
          if (comment.hide_reply_list && comment.hide_reply_list.length > 0) {
            const hideReplyList = comment.hide_reply_list.slice();
            const newReplies = [...(comment.replies || []), ...hideReplyList, ...replyList].filter((comment, index, self) =>
              index === self.findIndex((t) => t.reply_id === comment.reply_id)
            )
            return {
              ...comment, replies: newReplies, hide_reply_list: []
            };
          }
          const newReplies = [...(comment.replies || []), ...replyList].filter((comment, index, self) =>
            index === self.findIndex((t) => t.reply_id === comment.reply_id)
          )
          return { ...comment, replies: newReplies };
        }
        return comment;
      }),
      newComments: state.newComments.map((comment) => {
        if (comment.comment_id === commentId) {
          if (comment.hide_reply_list && comment.hide_reply_list.length > 0) {
            const hideReplyList = comment.hide_reply_list.slice();
            const newReplies = [...(comment.replies || []), ...hideReplyList, ...replyList].filter((comment, index, self) =>
              index === self.findIndex((t) => t.reply_id === comment.reply_id)
            )
            return { ...comment, replies: newReplies, hide_reply_list: [] };
          }
          const newReplies = [...(comment.replies || []), ...replyList].filter((comment, index, self) =>
            index === self.findIndex((t) => t.reply_id === comment.reply_id)
          )
          return { ...comment, replies: newReplies };
        }
        return comment;
      }),
    }));
  },

  deleteComment: (commentId: string) => {
    set((state) => ({
      hotComments: state.hotComments.filter((comment) => comment.comment_id !== commentId),
      newComments: state.newComments.filter((comment) => comment.comment_id !== commentId),
    }));
  },

  deleteReply: (commentId: string, replyId: string) => {
    set((state) => ({
      hotComments: state.hotComments.map((comment) => {
        if (comment.comment_id === commentId && comment.replies) {
          const updatedReplies = comment.replies.filter((reply) => reply.reply_id !== replyId);
          return {
            ...comment,
            replies: updatedReplies,
            reply_n: updatedReplies.length,
          };
        }
        return comment;
      }),
      newComments: state.newComments.map((comment) => {
        if (comment.comment_id === commentId && comment.replies) {
          const updatedReplies = comment.replies.filter((reply) => reply.reply_id !== replyId);
          return {
            ...comment,
            replies: updatedReplies,
            reply_n: updatedReplies.length,
          };
        }
        return comment;
      }),
    }));
  },

  updateComment: (commentId: string, updates: Partial<Comment> & { type?: string }) => {
    set((state) => ({
      hotComments: state.hotComments.map((comment) =>
        comment.comment_id === commentId
          ? { ...comment, ...updates } as Comment
          : comment
      ),
      newComments: state.newComments.map((comment) =>
        comment.comment_id === commentId
          ? { ...comment, ...updates } as Comment
          : comment
      ),
    }));
  },

  updateReply: (commentId: string, replyId: string, updates: Partial<CommentReply>) => {
    set((state) => ({
      hotComments: state.hotComments.map((comment) => {
        if (comment.comment_id === commentId && comment.replies) {
          return {
            ...comment,
            replies: comment.replies.map((reply) =>
              reply.reply_id === replyId
                ? { ...reply, ...updates }
                : reply
            ),
          };
        }
        return comment;
      }),
      newComments: state.newComments.map((comment) => {
        if (comment.comment_id === commentId && comment.replies) {
          return {
            ...comment,
            replies: comment.replies.map((reply) =>
              reply.reply_id === replyId
                ? { ...reply, ...updates }
                : reply
            ),
          };
        }
        return comment;
      }),
    }));
  },

  clearComments: () => {
    set({ hotComments: [], newComments: [] });
  },

  getCommentById: (commentId: string) => {
    return get().hotComments.find((comment) => comment.comment_id === commentId) || get().newComments.find((comment) => comment.comment_id === commentId);
  },

  getReplyById: (commentId: string, replyId: string) => {
    const comment = get().hotComments.find((comment) => comment.comment_id === commentId) || get().newComments.find((comment) => comment.comment_id === commentId);
    console.log("🚀 ~ useCommentStore ~ comment:", commentId, replyId, comment)
    return comment?.replies?.find((reply) => reply.reply_id === replyId);
  },
})));

// ============ Helper Functions ============
export const useCommentActions = () => {
  const {
    setHotComments,
    setNewComments,
    addHotComments,
    addNewComments,
    addComment,
    addReply,
    deleteComment,
    deleteReply,
    updateComment,
    updateReply,
    clearComments,
    setReplyList,
  } = useCommentStore();

  return {
    setHotComments,
    setNewComments,
    addHotComments,
    addNewComments,
    addComment,
    addReply,
    deleteComment,
    deleteReply,
    updateComment,
    updateReply,
    clearComments,
    setReplyList,
  };
};

export const useCommentSelectors = () => {
  const { getCommentById, getReplyById, hotComments, newComments } = useCommentStore();

  return {
    hotComments,
    newComments,
    getCommentById,
    getReplyById,
    totalComments: newComments?.length || 0,
    totalReplies: (newComments || []).reduce((total, comment) => total + (comment.replies?.length || 0), 0),
  };
};

// devtools(useCommentStore, { name: "comment" });